import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Alert, Badge, Button, Card, Col, Form, ListGroup, Modal, OverlayTrigger, ProgressBar, Row, Spinner, Tooltip } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faClock, faExclamationCircle, faPercentage, faTimesCircle, faUsers } from "@fortawesome/free-solid-svg-icons";
import { Clock, Trash } from "react-feather";
import generateExcel from "zipcelx";
import { getRsvpAnswers } from "../utils/rsvpAnswers.js";
import { RECORD_REGISTRATION_LABELS, RECORD_RSVP_LABELS, participantRegistrationStatus } from "../utils/participantRecords.js";
import { formatInTimeZone } from "date-fns-tz";
import { confirmAlert } from "react-confirm-alert";
import { correctParticipantRsvp, getEventRsvpResponses, getParticipantRsvpHistory, resetEventRsvpResponses } from "../api/rsvp.js";
import { buildExcelExportMetadataRows, ColumnSortingTable, getExcelCell } from "./Table.js";
import ReportDateRangeFilter from "./ReportDateRangeFilter.js";
import EventRsvpResponsesDistribution from "./EventRsvpResponsesDistribution.js";
import { ShowConfirm } from "./Alerts.js";
import { getDefaultDateRangeInTimeZone } from "../assets/functions.js";

const CHOICES = {
  yes: { label: RECORD_RSVP_LABELS.yes, color: "success" },
  no: { label: RECORD_RSVP_LABELS.no, color: "dark" },
  unanswered: { label: "Sem resposta", color: "secondary" },
  other: { label: "Revisão necessária", color: "secondary" },
};
const storedValueLabel = (value) => value === null || value === undefined ? "" : typeof value === "string" ? value : JSON.stringify(value);
const answerLabel = (response, fallback) => response?.answer_label || storedValueLabel(response?.value) || ({ yes: "Sim", no: "Não" }[fallback]) || "Sem resposta";
const responseStatus = (choice, isAttendance) => !isAttendance && ["yes", "no"].includes(choice)
  ? { label: "Respondido", color: "primary" }
  : CHOICES[choice] || CHOICES.other;
const numberLabel = (value) => Number(value || 0).toLocaleString("pt-BR");
const percentageLabel = (value) => value > 0 && value < 0.1 ? "<0,1%" : `${value.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
const summaryFigures = (summary, isAttendance) => {
  const total = Number(summary.total || 0);
  const answered = Number(summary.yes || 0) + Number(summary.no || 0);
  const highlighted = isAttendance ? Number(summary.yes || 0) : answered;
  const percentage = total > 0 ? highlighted / total * 100 : 0;
  return { total, answered, highlighted, percentage };
};
const currentAnswerLabel = (item) => item.response?.answer_label
  || (["yes", "no"].includes(item.current_choice) ? answerLabel(item.response, item.current_choice) : "—");
const participantAccessStatusLabel = (item) => {
  if (item.registration?.status === null || item.registration?.status === undefined || item.registration?.status === "") return "—";
  return RECORD_REGISTRATION_LABELS[participantRegistrationStatus(item.registration)];
};

const participantColumnValue = (column, participant) => typeof column.accessor === "function"
  ? column.accessor(participant)
  : String(column.accessor || "").split(".").reduce((value, key) => value?.[key], participant);
const RSVP_ADDITIONAL_LEADING_ACCESSORS = new Set(["user_id", "external_user_id", "origin"]);
const RSVP_ADDITIONAL_TRAILING_ACCESSORS = new Set([
  "rank_position", "total_value", "total_exhibitors_collected", "access_code", "last_access_formatted",
  "access_code_used", "access_code_expires_formatted", "push_subscription",
]);
const buildParticipantInfoColumns = (columns) => columns
  .filter((column) => column.id !== "icons" && column.accessor !== "icons" && column.accessor !== "email" && column.Header)
  .map((column, index) => {
    const valueForRow = (item) => participantColumnValue(column, item.registration || {});
    return {
      ...column,
      id: `participant:${column.id || (typeof column.accessor === "string" ? column.accessor : index)}:${index}`,
      registrationAccessor: column.accessor,
      accessor: valueForRow,
      disableFilters: true,
      disableSortBy: true,
      Cell: typeof column.Cell === "function" ? (cell) => React.createElement(column.Cell, {
        ...cell,
        row: { ...cell.row, original: cell.row.original.registration || {} },
      }) : column.Cell,
      exportValue: (item) => typeof column.exportValue === "function"
        ? column.exportValue(item.registration || {}, valueForRow(item))
        : valueForRow(item),
    };
  });

const RsvpSummary = ({ summary, isAttendance }) => {
  const { total, answered, highlighted, percentage } = summaryFigures(summary, isAttendance);
  const stats = isAttendance ? [
    { key: "yes", label: "Participação confirmada", value: summary.yes, icon: faCheckCircle, highlight: true },
    { key: "no", label: RECORD_RSVP_LABELS.no, value: summary.no, icon: faTimesCircle },
    { key: "unanswered", label: "Sem resposta", value: summary.unanswered, icon: faClock },
    { key: "other", label: "Revisão necessária", value: summary.other, icon: faExclamationCircle },
  ] : [
    { key: "answered", label: "Respondidos", value: answered, icon: faCheckCircle },
    { key: "unanswered", label: "Sem resposta", value: summary.unanswered, icon: faClock },
    { key: "other", label: "Revisão necessária", value: summary.other, icon: faExclamationCircle },
    { key: "rate", label: "Taxa de resposta", value: percentageLabel(percentage), icon: faPercentage },
  ];

  return <Card className="bentoPanel bentoPanelGradient credential-report-panel mb-3">
    <Card.Body>
      <div className="bentoHeader mb-3">
        <div className="bentoIcon"><FontAwesomeIcon icon={faUsers} /></div>
        <div>
          <h5 className="mb-1 fw-bold">{isAttendance ? "Confirmação de participação" : "Respostas do RSVP"}</h5>
          <div className="small text-muted">Resumo geral deste RSVP · {numberLabel(total)} pessoas na base</div>
        </div>
      </div>
      <div className="d-flex flex-wrap align-items-baseline gap-2 mb-2">
        <h2 className="mb-0 fw-bold">{numberLabel(highlighted)} de {numberLabel(total)}</h2>
        <span className="text-muted">{isAttendance ? "participantes confirmaram participação" : "participantes responderam a este RSVP"}</span>
        <Badge bg={isAttendance ? "success" : "primary"} className="ms-sm-auto">{percentageLabel(percentage)} {isAttendance ? "de confirmação" : "de resposta"}</Badge>
      </div>
      <ProgressBar className="mb-3" now={percentage} variant={isAttendance ? "success" : "primary"} aria-label={isAttendance ? "Percentual de participantes que confirmaram participação" : "Percentual de participantes que responderam ao RSVP"} />
      <div className="bentoStatsGrid bentoStatsGridSingleRow">
        {stats.map((stat) => <div key={stat.key} className={`bentoStatCard ${stat.highlight ? "bg-success text-white" : ""}`}>
          <div className={`bentoStatHeader ${stat.highlight ? "text-white" : ""}`}><FontAwesomeIcon icon={stat.icon} /></div>
          <div className="bentoStatBody">
            <h2 className={stat.highlight ? "text-white" : ""}>{stat.key === "rate" ? stat.value : numberLabel(stat.value)}</h2>
            <span className={stat.highlight ? "text-white" : ""}>{stat.label}</span>
          </div>
        </div>)}
      </div>
    </Card.Body>
  </Card>;
};
const CHANNELS = {
  public_rsvp: "Página de confirmação",
  email_link: "E-mail",
  whatsapp: "WhatsApp",
  panel_correction: "Administrador",
  other: "Outro canal",
  none: "Não informado",
};
const RSVP_RESPONSE_PAGE_SIZE = 100;
const rsvpReportSource = (source) => {
  if (!source) return "none";
  if (source === "email") return "email_link";
  if (source === "administrator") return "panel_correction";
  if (String(source).startsWith("whatsapp")) return "whatsapp";
  return ["public_rsvp", "email_link", "panel_correction"].includes(source) ? source : "other";
};
const filterRsvpChoice = (rows, id, filterValue) => {
  if (!filterValue) return rows;
  const normalizedFilter = String(filterValue);
  if (normalizedFilter.startsWith("answer:")) {
    const answerId = normalizedFilter.slice(7);
    return rows.filter((row) => {
      const responseAnswer = row.original.response?.option_id
        || (["yes", "no"].includes(row.original.current_choice) ? row.original.current_choice : "");
      return String(responseAnswer) === answerId;
    });
  }
  return rows.filter((row) => String(row.values[id] ?? "") === normalizedFilter);
};
const RsvpReportColumnFilter = ({ column: { rsvpFilter, setFilter }, gotoPage }) => <Form.Select
  className="mt-2"
  aria-label={rsvpFilter.label}
  value={rsvpFilter.value}
  onChange={(event) => {
    const value = event.target.value;
    setFilter(value || undefined);
    rsvpFilter.onChange(value);
    gotoPage?.(0);
  }}
>
  <option value="">{rsvpFilter.allLabel || "Todos"}</option>
  {Object.entries(rsvpFilter.options).map(([value, option]) => <option key={value} value={value}>{option.label || option}</option>)}
</Form.Select>;
const channelLabel = (source) => ({
  email: "E-mail",
  administrator: "Administrador",
  public_rsvp: "Página de confirmação",
  email_link: "E-mail",
  whatsapp_quick_reply: "WhatsApp",
  panel_correction: "Administrador",
  rsvp_configuration: "Configuração do RSVP",
}[source] || (String(source || "").startsWith("whatsapp") ? "WhatsApp" : source ? "Outro canal" : "—"));
const inputMethodLabel = (method) => ({ confirmation_page: "Página de confirmação", whatsapp_button: "Botão no WhatsApp", administrator: "Correção administrativa", configuration_update: "Alteração da configuração" }[method] || "—");
const inputRejectionLabel = (code) => ({
  RSVP_TOKEN_EXPIRED: "Prazo do convite encerrado",
  RSVP_INVITATION_REVOKED: "Convite revogado",
  RSVP_SETTINGS_DISABLED: "RSVP desativado na configuração anterior",
  RSVP_SETTINGS_LOCKED: "Alteração de resposta não permitida",
  RSVP_SETTINGS_STALE: "Configuração do convite incompatível",
  RSVP_RESPONSE_NEEDS_REVIEW: "Resposta precisa de revisão",
  RSVP_CHOICES_INVALID: "Opções de resposta incompatíveis",
}[code] || "Entrada rejeitada");
const responseError = (failure, fallback) => ({
  RSVP_ATTENDANCE_REQUIRED: "Selecione um RSVP para consultar as respostas. Nenhum RSVP foi definido como confirmação principal de participação.",
  RSVP_STORAGE_MIGRATION_REQUIRED: "A estrutura de histórico RSVP precisa ser atualizada no servidor antes de continuar.",
  RSVP_RESPONSE_NEEDS_REVIEW: "Esta resposta precisa ser revisada antes de receber novas confirmações.",
  RSVP_REPORT_TIME_ZONE_INVALID: "O fuso horário do evento não é válido para filtrar as datas.",
  RSVP_REPORT_DATE_RANGE_INVALID: "Informe um intervalo de datas válido para as respostas RSVP.",
}[failure.response?.data?.code] || failure.response?.data?.message || fallback);
const dateLabel = (value, timezone, withSeconds = false) => {
  if (!value) return "—";
  const normalized = typeof value === "string" && /^\d{4}-\d{2}-\d{2} /.test(value) ? `${value.replace(" ", "T")}Z` : value;
  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? "—" : formatInTimeZone(date, timezone, withSeconds ? "dd/MM/yyyy HH:mm:ss" : "dd/MM/yyyy HH:mm");
};
const requestKey = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const historyAnswerLabel = (entry) => {
  if (entry.choice === "reset") return "Resposta removida";
  return entry.answer_label || storedValueLabel(entry.value) || ({ yes: "Sim", no: "Não" }[entry.choice]) || "Não informada";
};
const historyResult = (entry) => {
  if (entry.outcome === "rejected") return { label: "Não aplicada", color: "danger", description: inputRejectionLabel(entry.rejection_code) };
  if (entry.outcome === "configuration_updated") return entry.needs_review
    ? { label: "Revisão necessária", color: "warning", description: "A configuração do RSVP mudou." }
    : { label: "Configuração atualizada", color: "secondary" };
  if (entry.choice === "reset") return { label: "Resposta removida", color: "secondary" };
  if (entry.is_change === false || entry.is_change === 0) return { label: "Resposta repetida", color: "secondary" };
  return { label: "Resposta aceita", color: "success" };
};

const RsvpHistoryMetadata = ({ entry, eventTimezone }) => {
  const registeredAt = dateLabel(entry.accepted_at || entry.created_at, eventTimezone, true);
  const details = [
    { label: "Valor registrado", value: storedValueLabel(entry.value) },
    { label: "Forma de resposta", value: entry.input_method ? inputMethodLabel(entry.input_method) : null },
    { label: "Recebida em", value: entry.received_at && dateLabel(entry.received_at, eventTimezone, true) !== registeredAt ? dateLabel(entry.received_at, eventTimezone, true) : null },
    { label: "Enviada no WhatsApp em", value: entry.occurred_at ? dateLabel(entry.occurred_at, eventTimezone, true) : null },
    { label: "Versão do RSVP", value: entry.rsvp_version_id },
    { label: "Revisão da configuração", value: entry.config_revision ?? entry.definition_revision },
    { label: "Administrador (ID)", value: entry.administrator_id },
  ].filter(({ value }) => value !== null && value !== undefined && value !== "");
  if (!details.length) return null;
  return <details className="mt-2">
    <summary className="small text-muted">Detalhes do registro</summary>
    <dl className="small mt-2 mb-0">
      {details.map(({ label, value }) => <div key={label} className="mb-2">
        <dt className="fw-semibold">{label}</dt>
        <dd className="text-break mb-0">{value}</dd>
      </div>)}
    </dl>
  </details>;
};

const RsvpHistoryContent = ({ history, historyDetails, eventTimezone, isAttendance }) => {
  const current = historyDetails.current_response || {};
  const currentChoice = current.current_choice || "unanswered";
  const status = responseStatus(currentChoice, isAttendance);
  const channels = Object.entries(historyDetails.latest_inputs || {}).filter(([, input]) => Boolean(input));
  const hasCurrentRecord = Boolean(current.response_revision || current.response_revision_id || current.accepted_at || current.responded_at);

  return <>
    <section className="mb-3" aria-label="Resposta atual">
        <h6 className="mb-2">Resposta atual</h6>
        <div className="d-flex flex-wrap align-items-center gap-2">
          <strong className="text-break">{currentChoice === "unanswered" ? "Sem resposta registrada" : historyAnswerLabel(current)}</strong>
          <Badge bg={currentChoice === "other" ? "warning" : status.color} text={currentChoice === "other" ? "dark" : undefined}>{status.label}</Badge>
        </div>
        {currentChoice === "other" && <p className="small text-muted mt-2 mb-0">Esta resposta precisa de revisão antes de contar no relatório.</p>}
        {hasCurrentRecord && <>
          <div className="small text-muted mt-2">{channelLabel(current.origin_channel || current.source)} · {inputMethodLabel(current.input_method)}</div>
          <Row as="dl" className="g-2 small mt-2 mb-0">
            <Col xs={12} sm={6}>
              <dt className="text-muted fw-normal">{currentChoice === "unanswered" ? "Resposta removida em" : "Resposta aceita em"}</dt>
              <dd className="mb-0">{dateLabel(current.accepted_at || current.responded_at, eventTimezone, true)}</dd>
            </Col>
            {current.configuration_updated_at && <Col xs={12} sm={6}>
              <dt className="text-muted fw-normal">Configuração atualizada em</dt>
              <dd className="mb-0">{dateLabel(current.configuration_updated_at, eventTimezone, true)}</dd>
            </Col>}
          </Row>
          <RsvpHistoryMetadata entry={current} eventTimezone={eventTimezone} />
        </>}
    </section>
    <hr className="my-3" />

    {channels.length > 1 && <details className="border rounded p-3 mb-4">
      <summary className="fw-semibold">Última resposta aceita por canal</summary>
      <Row className="g-3 mt-0">
        {channels.map(([origin, input]) => <Col key={origin} xs={12} sm={6}>
          <div className="bg-light rounded p-3 h-100 small">
            <div className="fw-semibold mb-1">{channelLabel(origin)}</div>
            <div className="text-break">{historyAnswerLabel(input)}</div>
            <div className="text-muted mt-2">{dateLabel(input.accepted_at || input.created_at, eventTimezone, true)}</div>
            <div className="text-muted">{inputMethodLabel(input.input_method)}</div>
          </div>
        </Col>)}
      </Row>
    </details>}

    <section aria-label="Histórico de respostas">
      <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2 mb-2">
        <h6 className="mb-0">Histórico de respostas</h6>
        <span className="small text-muted">{eventTimezone}</span>
      </div>
      {history.length ? <ListGroup as="ol" className="mb-0" aria-label="Respostas, mais recentes primeiro">
        {history.map((entry) => {
          const outcome = historyResult(entry);
          const isCurrent = entry.outcome !== "rejected" && entry.response_revision_id != null
            && String(entry.response_revision_id) === String(current.response_revision_id);
          return <ListGroup.Item as="li" key={entry.response_revision_id} className="p-2">
            <div className="d-flex flex-wrap justify-content-between align-items-baseline gap-2">
              <strong className="text-break">{historyAnswerLabel(entry)}</strong>
              <small className="text-muted">{dateLabel(entry.accepted_at || entry.created_at, eventTimezone, true)}</small>
            </div>
            <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                <span className="small text-muted">{channelLabel(entry.origin_channel || entry.source)}</span>
                <Badge bg={outcome.color} text={outcome.color === "warning" ? "dark" : undefined}>{outcome.label}</Badge>
                {isCurrent && <Badge bg="primary">Atual</Badge>}
            </div>
            {outcome.description && <div className="small text-muted mt-2">{outcome.description}</div>}
            <RsvpHistoryMetadata entry={entry} eventTimezone={eventTimezone} />
          </ListGroup.Item>;
        })}
      </ListGroup> : <div className="text-center text-muted border rounded p-4">Nenhuma resposta registrada para este RSVP.</div>}
    </section>
  </>;
};

const EventRsvpResponsesCard = React.forwardRef(function EventRsvpResponsesCard({ eventId, eventTimezone = "America/Sao_Paulo", refreshKey, onStateChange, participantColumns = [], participantFormFields = [], participantEmailLabel = "E-mail", participantRecords = [], showMoreInformation = false, showDefinitionSelector = true, showSummary = true, showDistribution = false, onResponsesChanged, tableContainerRef }, ref) {
  const [definitionId, setDefinitionId] = useState("");
  const [registryState, setRegistryState] = useState({ eventId, registry: null, loading: true, error: "" });
  const [registryReload, setRegistryReload] = useState(0);
  const registry = registryState.eventId === eventId ? registryState.registry : null;
  const definitionOptions = useMemo(() => (registry?.definitions || []).map((definition) => ({
    value: definition.definition_id,
    label: definition.name,
    typeLabel: definition.definition_id === registry.attendance_definition_id ? "Confirmação de presença" : "Pergunta geral",
  })), [registry]);
  const selectedDefinition = registry?.definitions?.find((definition) => definition.definition_id === definitionId);
  const selectedId = selectedDefinition?.definition_id || "";
  const isAttendance = Boolean(selectedId && selectedId === registry?.attendance_definition_id);
  const [choice, setChoice] = useState("");
  const [responseDateRange, setResponseDateRange] = useState(() => getDefaultDateRangeInTimeZone(14, 14, eventTimezone));
  const [pendingResponseDateRange, setPendingResponseDateRange] = useState(responseDateRange);
  const [dateRangeError, setDateRangeError] = useState("");
  const responseStart = responseDateRange.startDate?.toISOString() || "";
  const responseEnd = responseDateRange.endDate?.toISOString() || "";
  const [source, setSource] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  const [participant, setParticipant] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyDetails, setHistoryDetails] = useState(null);
  const [historyLoadingMore, setHistoryLoadingMore] = useState(false);
  const historyRequestRef = useRef(0);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [correction, setCorrection] = useState("");
  const [saving, setSaving] = useState(false);
  const [correctionKey, setCorrectionKey] = useState("");
  const [exporting, setExporting] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [showBulkReset, setShowBulkReset] = useState(false);
  const [resettingParticipant, setResettingParticipant] = useState(false);
  const [bulkResetting, setBulkResetting] = useState(false);
  const [resetRequestKey, setResetRequestKey] = useState("");
  const [resetActionError, setResetActionError] = useState("");
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    const defaults = getDefaultDateRangeInTimeZone(14, 14, eventTimezone);
    setDefinitionId(""); setChoice(""); setResponseDateRange(defaults); setPendingResponseDateRange(defaults); setDateRangeError(""); setSource(""); setResult(null); setParticipant(null); setResetTarget(null); setShowBulkReset(false); setNotice(null);
  }, [eventId, eventTimezone]);
  useEffect(() => {
    const controller = new AbortController();
    setRegistryState({ eventId, registry: null, loading: true, error: "" });
    const acceptRegistry = (loaded) => {
      if (!Array.isArray(loaded?.definitions)) throw new Error("RSVP registry unavailable");
      if (!controller.signal.aborted) {
        setRegistryState({ eventId, registry: loaded, loading: false, error: "" });
        setDefinitionId((current) => loaded.definitions.some((definition) => definition.definition_id === current)
          ? current
          : loaded.definitions.find((definition) => definition.definition_id === loaded.attendance_definition_id)?.definition_id || "");
      }
    };
    getEventRsvpResponses({ eventId, pageSize: 1, signal: controller.signal })
      .then((response) => acceptRegistry(response.data?.registry))
      .catch((failure) => {
        if (controller.signal.aborted) return;
        if (failure.response?.data?.code === "RSVP_ATTENDANCE_REQUIRED" && Array.isArray(failure.response.data.registry?.definitions)) {
          acceptRegistry(failure.response.data.registry);
          return;
        }
        setRegistryState({ eventId, registry: null, loading: false, error: "Não foi possível carregar os RSVPs do evento." });
      });
    return () => controller.abort();
  }, [eventId, refreshKey, registryReload]);
  useEffect(() => {
    if (!selectedId) {
      setResult(null); setError(""); setLoading(false);
      return undefined;
    }
    const controller = new AbortController();
    setLoading(true);
    setError("");
    const timer = setTimeout(() => {
      const loadAllResponses = async () => {
        let firstPage;
        const participants = [];
        for (let currentPage = 1; ; currentPage += 1) {
          const response = await getEventRsvpResponses({
            eventId,
            definitionId: selectedId,
            responseStart: responseStart || undefined,
            responseEnd: responseEnd || undefined,
            timeZone: eventTimezone,
            page: currentPage,
            pageSize: RSVP_RESPONSE_PAGE_SIZE,
            signal: controller.signal,
          });
          if (controller.signal.aborted) return;
          if (!firstPage) firstPage = response.data;
          const pageParticipants = response.data?.participants || [];
          participants.push(...pageParticipants);
          if (!pageParticipants.length || participants.length >= Number(response.data?.total || 0)) break;
        }
        if (!controller.signal.aborted) {
          setResult({
            ...firstPage,
            participants,
            total: participants.length,
            query: { eventId, definitionId: selectedId, responseStart, responseEnd, eventTimezone },
          });
        }
      };
      loadAllResponses()
        .catch((failure) => {
          if (controller.signal.aborted) return;
          setError(responseError(failure, "Não foi possível carregar as respostas RSVP."));
        })
        .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    }, 180);
    return () => { clearTimeout(timer); controller.abort(); };
  }, [definitionId, selectedId, eventId, eventTimezone, refreshKey, reload, responseStart, responseEnd]);

  const answerOptions = useMemo(() => selectedDefinition ? getRsvpAnswers(selectedDefinition) : [], [selectedDefinition]);
  const hasDetailedAnswers = !isAttendance || answerOptions.filter((answer) => answer.counts_as_attendance === true).length > 1 || answerOptions.filter((answer) => answer.counts_as_attendance !== true).length > 1;
  const answerFilters = useMemo(() => ({
    ...(isAttendance ? CHOICES : { unanswered: CHOICES.unanswered, other: CHOICES.other }),
    ...(hasDetailedAnswers ? Object.fromEntries(answerOptions.map((item) => ["answer:" + item.id, { label: `Resposta: ${item.label}` }])) : {}),
  }), [answerOptions, hasDetailedAnswers, isAttendance]);
  const resultMatchesDefinition = Boolean(selectedId) && result?.query?.eventId === eventId
    && result.query.definitionId === definitionId
    && result.query.eventTimezone === eventTimezone;
  const resultMatchesFilters = resultMatchesDefinition
    && result.query.responseStart === responseStart && result.query.responseEnd === responseEnd;
  const participantRecordsById = useMemo(() => new Map(participantRecords.map((item) => [String(item.user_id), item])), [participantRecords]);
  const participants = useMemo(() => resultMatchesDefinition
    ? (result?.participants || []).map((item) => ({ ...item, registration: participantRecordsById.get(String(item.user_id)) }))
    : [], [participantRecordsById, result, resultMatchesDefinition]);
  const participantFormFieldsById = useMemo(() => new Map(participantFormFields
    .filter((field) => field?.inputID !== undefined && field?.inputID !== null)
    .map((field) => [String(field.inputID), field])), [participantFormFields]);
  const participantInfoColumns = useMemo(() => buildParticipantInfoColumns(participantColumns.filter((column) =>
    typeof column.accessor === "string" && participantFormFieldsById.get(column.accessor)?.showInUserReport === true,
  )), [participantColumns, participantFormFieldsById]);
  const additionalParticipantMetadataColumns = useMemo(() => buildParticipantInfoColumns(participantColumns.filter((column) => {
    if (typeof column.accessor !== "string") return false;
    return !participantFormFieldsById.has(column.accessor)
      && (RSVP_ADDITIONAL_LEADING_ACCESSORS.has(column.accessor) || RSVP_ADDITIONAL_TRAILING_ACCESSORS.has(column.accessor));
  })), [participantColumns, participantFormFieldsById]);
  const additionalParticipantMetadataLeadingColumns = useMemo(() => additionalParticipantMetadataColumns.filter((column) =>
    RSVP_ADDITIONAL_LEADING_ACCESSORS.has(column.registrationAccessor),
  ), [additionalParticipantMetadataColumns]);
  const additionalParticipantMetadataTrailingColumns = useMemo(() => additionalParticipantMetadataColumns.filter((column) =>
    RSVP_ADDITIONAL_TRAILING_ACCESSORS.has(column.registrationAccessor),
  ), [additionalParticipantMetadataColumns]);
  const additionalParticipantFormColumns = useMemo(() => buildParticipantInfoColumns(participantColumns.filter((column) =>
    typeof column.accessor === "string"
    && participantFormFieldsById.has(column.accessor)
    && participantFormFieldsById.get(column.accessor)?.showInUserReport !== true,
  )), [participantColumns, participantFormFieldsById]);
  const summary = resultMatchesDefinition && !error ? result?.summary : null;
  const resetCount = summary ? Number(summary.yes || 0) + Number(summary.no || 0) + Number(summary.other || 0) : 0;
  const changeDefinition = (option) => {
    const defaults = getDefaultDateRangeInTimeZone(14, 14, eventTimezone);
    setDefinitionId(option?.value || ""); setChoice(""); setSource(""); setResult(null); setError(""); setNotice(null);
    setResponseDateRange(defaults); setPendingResponseDateRange(defaults); setDateRangeError("");
    setParticipant(null); setResetTarget(null); setShowBulkReset(false);
  };
  const applyResponseDateRange = () => {
    const { startDate, endDate } = pendingResponseDateRange;
    if ((startDate && (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())))
      || (endDate && (!(endDate instanceof Date) || Number.isNaN(endDate.getTime())))
      || (startDate && endDate && startDate > endDate)) {
      setDateRangeError("Informe um intervalo de datas válido para as respostas RSVP.");
      return;
    }
    setDateRangeError(""); setResponseDateRange({ startDate, endDate });
  };
  const restoreResponseDateRange = () => {
    const defaults = getDefaultDateRangeInTimeZone(14, 14, eventTimezone);
    setPendingResponseDateRange(defaults); setResponseDateRange(defaults); setDateRangeError("");
  };
  const canExport = Boolean(selectedId && !exporting && !loading && !error && resultMatchesFilters);
  const canResetAll = Boolean(selectedId && resultMatchesFilters && result?.can_edit && resetCount > 0 && !loading && !error && !resettingParticipant && !bulkResetting);
  useEffect(() => { onStateChange?.({ total: resultMatchesDefinition ? Number(result?.total || 0) : 0, loading: loading && !resultMatchesDefinition, exporting, canExport, resetCount, canResetAll, resetting: resettingParticipant || bulkResetting }); }, [bulkResetting, canExport, canResetAll, exporting, loading, onStateChange, resettingParticipant, resetCount, result?.total, resultMatchesDefinition]);

  const openBulkResetConfirmation = () => {
    if (!canResetAll || !selectedDefinition || showBulkReset || bulkResetting) return;
    const confirmation = {
      definitionId: selectedId,
      definitionName: selectedDefinition.name,
      expectedCount: resetCount,
      requestKey: requestKey(),
    };
    setShowBulkReset(true);
    confirmAlert({
      closeOnEscape: false,
      closeOnClickOutside: false,
      customUI: ({ onClose }) => <ShowConfirm
        title="Excluir histórico de resposta?"
        description={`Serão excluídas ${numberLabel(confirmation.expectedCount)} respostas atuais do RSVP “${confirmation.definitionName}” no evento #${eventId}. A ação considera o RSVP inteiro, independentemente dos filtros da tabela. Os participantes continuarão inscritos e poderão responder novamente; o histórico de alterações, os convites e os registros das campanhas serão preservados.`}
        textConfirm="Excluir respostas"
        action={() => {
          setShowBulkReset(false);
          void confirmBulkReset(confirmation);
        }}
        onClose={() => {
          setShowBulkReset(false);
          onClose();
        }}
      />,
    });
  };

  const columns = useMemo(() => [
    {
      Header: isAttendance ? "Confirmação" : "Status da resposta", accessor: "current_choice", Filter: RsvpReportColumnFilter, filter: filterRsvpChoice,
      rsvpFilter: { label: "Filtrar por situação ou resposta", allLabel: "Todas as respostas", options: answerFilters, value: choice, onChange: setChoice },
      Cell: ({ value }) => { const status = responseStatus(value, isAttendance); return <Badge bg={status.color}>{status.label}</Badge>; },
    },
    {
      Header: "Resposta atual", id: "answer", accessor: currentAnswerLabel, disableFilters: true,
      Cell: ({ row, value }) => <>{value}{row.original.current_choice === "other" && row.original.response?.answer_label && <div className="small text-muted">Resposta aguardando revisão</div>}</>,
    },
    {
      Header: "Última resposta aceita em", accessor: (item) => dateLabel(item.accepted_at || item.responded_at || item.response?.responded_at, eventTimezone), id: "responded_at", disableFilters: true,
    },
    {
      Header: "Canal", accessor: (item) => rsvpReportSource(item.origin_channel || item.source || item.response?.source), id: "source", Filter: RsvpReportColumnFilter, filter: "equals",
      rsvpFilter: { label: "Filtrar por canal", options: CHANNELS, value: source, onChange: setSource },
      Cell: ({ row }) => <>{channelLabel(row.original.origin_channel || row.original.source || row.original.response?.source)}{(row.original.input_method || row.original.response?.input_method) && <div className="small text-muted">{inputMethodLabel(row.original.input_method || row.original.response?.input_method)}</div>}</>,
    },
    {
      Header: "", id: "icons", disableFilters: true, disableExport: true,
      Cell: ({ row }) => {
        const actionLocked = !resultMatchesFilters || Boolean(error) || loading || saving || exporting || resettingParticipant || bulkResetting;
        const canResetParticipant = result?.can_edit && row.original.current_choice !== "unanswered";
        const openHistory = () => {
          if (actionLocked) return;
          setHistory([]);
          setHistoryDetails(null);
          setHistoryError("");
          setHistoryLoading(true);
          setParticipant({ ...row.original, definition_id: selectedId });
          setCorrection("");
          setCorrectionKey(requestKey());
        };
        const openResetConfirmation = () => {
          if (actionLocked || !canResetParticipant) return;
          setResetTarget({ ...row.original, definition_id: selectedId });
          setResetActionError("");
          setResetRequestKey(requestKey());
        };
        return <>
          {canResetParticipant && <OverlayTrigger overlay={<Tooltip>Remover resposta atual</Tooltip>}>
            <Trash
              role="button"
              className="align-middle cursor-pointer me-2"
              size={18}
              tabIndex={actionLocked ? -1 : 0}
              aria-label={`Remover resposta atual de ${row.original.name || row.original.user_id}`}
              aria-disabled={actionLocked}
              onClick={openResetConfirmation}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openResetConfirmation();
                }
              }}
            />
          </OverlayTrigger>}
          <OverlayTrigger overlay={<Tooltip>Histórico</Tooltip>}>
            <Clock
              role="button"
              className="align-middle cursor-pointer"
              size={18}
              tabIndex={actionLocked ? -1 : 0}
              aria-label={`Histórico de ${row.original.name || row.original.user_id}`}
              aria-disabled={actionLocked}
              onClick={openHistory}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  openHistory();
                }
              }}
            />
          </OverlayTrigger>
        </>;
      },
    },
  ].map((column) => ({ ...column, disableSortBy: true })), [answerFilters, bulkResetting, choice, error, eventTimezone, exporting, isAttendance, loading, resettingParticipant, result?.can_edit, resultMatchesFilters, saving, selectedId, source]);
  const emailColumn = useMemo(() => ({
    Header: participantEmailLabel,
    accessor: "email",
    Cell: ({ value }) => value || "—",
    disableSortBy: true,
  }), [participantEmailLabel]);
  const accessStatusColumn = useMemo(() => ({
    Header: "Status",
    id: "participant_status",
    accessor: participantAccessStatusLabel,
    disableSortBy: true,
  }), []);
  const visibleColumns = useMemo(() => [
    emailColumn,
    ...participantInfoColumns,
    accessStatusColumn,
    ...(showMoreInformation ? additionalParticipantMetadataLeadingColumns : []),
    ...(showMoreInformation ? additionalParticipantFormColumns : []),
    ...(showMoreInformation ? additionalParticipantMetadataTrailingColumns : []),
    ...columns.filter((column) => column.id !== "icons"),
    ...columns.filter((column) => column.id === "icons"),
  ], [accessStatusColumn, additionalParticipantFormColumns, additionalParticipantMetadataLeadingColumns, additionalParticipantMetadataTrailingColumns, columns, emailColumn, participantInfoColumns, showMoreInformation]);

  useEffect(() => {
    if (!participant || !selectedId) return undefined;
    historyRequestRef.current += 1;
    const controller = new AbortController();
    setHistory([]); setHistoryDetails(null); setHistoryLoadingMore(false); setHistoryError(""); setHistoryLoading(true);
    getParticipantRsvpHistory({ eventId, userId: participant.user_id, definitionId: participant.definition_id, signal: controller.signal })
      .then((response) => { if (!controller.signal.aborted) { setHistory(response.data?.history || []); setHistoryDetails(response.data); } })
      .catch((failure) => { if (!controller.signal.aborted) setHistoryError(responseError(failure, "Não foi possível carregar o histórico.")); })
      .finally(() => { if (!controller.signal.aborted) setHistoryLoading(false); });
    return () => { controller.abort(); historyRequestRef.current += 1; };
  }, [eventId, participant, selectedId]);

  const loadMoreHistory = async () => {
    if (!participant || !historyDetails?.has_more || historyLoadingMore) return;
    const requestVersion = historyRequestRef.current;
    setHistoryLoadingMore(true); setHistoryError("");
    try {
      const response = await getParticipantRsvpHistory({ eventId, userId: participant.user_id, definitionId: participant.definition_id, beforeId: historyDetails.next_before_id });
      if (requestVersion !== historyRequestRef.current) return;
      setHistory((previous) => [...previous, ...(response.data?.history || [])]);
      setHistoryDetails((previous) => ({ ...previous, has_more: response.data?.has_more, next_before_id: response.data?.next_before_id }));
    } catch (failure) {
      if (requestVersion === historyRequestRef.current) setHistoryError(responseError(failure, "Não foi possível carregar mais entradas."));
    } finally { if (requestVersion === historyRequestRef.current) setHistoryLoadingMore(false); }
  };

  const saveCorrection = async () => {
    if (!correction || !participant || saving || historyLoading || !historyDetails) return;
    setSaving(true); setHistoryError("");
    try {
      await correctParticipantRsvp({ eventId, userId: participant.user_id, definitionId: participant.definition_id, choice: correction === "reset" ? "reset" : undefined, optionId: correction === "reset" ? undefined : correction, expectedRevision: historyDetails.current_response?.response_revision ?? participant.response_revision ?? 0, requestKey: correctionKey });
      setNotice({ variant: "success", message: correction === "reset" ? "Resposta atual removida. O histórico foi preservado." : "Resposta atualizada e registrada no histórico." });
      setParticipant(null); setReload((value) => value + 1);
      onResponsesChanged?.();
    } catch (failure) {
      const stale = failure.response?.data?.code === "RSVP_RESPONSE_STALE";
      setHistoryError(stale ? "A resposta mudou. Feche esta janela e atualize a lista antes de corrigir." : responseError(failure, "Não foi possível corrigir a resposta."));
      if (stale) { setParticipant(null); setError("A resposta mudou. A lista foi atualizada; abra o participante novamente para corrigir."); setReload((value) => value + 1); }
    } finally { setSaving(false); }
  };
  const confirmIndividualReset = async () => {
    if (!resetTarget || resettingParticipant || !resetRequestKey) return;
    setResettingParticipant(true);
    setResetActionError("");
    try {
      await correctParticipantRsvp({
        eventId,
        userId: resetTarget.user_id,
        definitionId: resetTarget.definition_id,
        choice: "reset",
        expectedRevision: Number(resetTarget.response_revision) || 0,
        requestKey: resetRequestKey,
      });
      setResetTarget(null);
      setNotice({ variant: "success", message: "Resposta atual removida. O histórico foi preservado." });
      setReload((value) => value + 1);
      onResponsesChanged?.();
    } catch (failure) {
      if (failure.response?.data?.code === "RSVP_RESPONSE_STALE") {
        setResetTarget(null);
        setNotice({ variant: "warning", message: "A resposta mudou desde que você abriu a confirmação. A lista foi atualizada; selecione o participante novamente." });
        setReload((value) => value + 1);
      } else {
        setResetActionError(responseError(failure, "Não foi possível remover a resposta."));
      }
    } finally { setResettingParticipant(false); }
  };
  const confirmBulkReset = async (confirmation) => {
    if (!canResetAll || !confirmation?.requestKey || confirmation.definitionId !== selectedId || bulkResetting) return;
    setBulkResetting(true);
    try {
      const response = await resetEventRsvpResponses({ eventId, definitionId: confirmation.definitionId, expectedCount: confirmation.expectedCount, requestKey: confirmation.requestKey });
      const count = Number(response.data?.reset_count ?? confirmation.expectedCount);
      setShowBulkReset(false);
      setNotice({ variant: "success", message: `${numberLabel(count)} respostas atuais do RSVP “${confirmation.definitionName}” excluídas. O histórico foi preservado.` });
      setReload((value) => value + 1);
      onResponsesChanged?.();
    } catch (failure) {
      if (failure.response?.data?.code === "RSVP_BULK_RESET_STALE") {
        setShowBulkReset(false);
        setNotice({ variant: "warning", message: "A quantidade de respostas mudou desde que você abriu a confirmação. A lista foi atualizada; revise o total e tente novamente." });
        setReload((value) => value + 1);
      } else {
        setNotice({ variant: "danger", message: responseError(failure, "Não foi possível excluir as respostas atuais deste RSVP.") });
      }
    } finally { setBulkResetting(false); }
  };
  const exportResponses = async () => {
    if (!selectedId || exporting || loading || !resultMatchesFilters) return;
    setExporting(true); setError("");
    try {
      const rows = [];
      let exportResult;
      for (let currentPage = 1; ; currentPage += 1) {
        const response = await getEventRsvpResponses({ eventId, definitionId: selectedId, choice: choice || undefined, responseStart: responseStart || undefined, responseEnd: responseEnd || undefined, source: source || undefined, timeZone: eventTimezone, page: currentPage, pageSize: 100 });
        if (currentPage === 1) exportResult = response.data;
        const items = response.data?.participants || [];
        rows.push(...items);
        if (!items.length || rows.length >= Number(response.data?.total || 0)) break;
      }
      const exportSummary = exportResult?.summary || {};
      const exportIsAttendance = selectedId === exportResult?.registry?.attendance_definition_id;
      const { total, highlighted, percentage } = summaryFigures(exportSummary, exportIsAttendance);
      const metadata = [
        { label: "RSVP", value: exportResult?.definition?.name || selectedId },
        { label: "RSVP principal de confirmação de participação", value: exportIsAttendance ? "Sim" : "Não" },
        { label: "Resumo geral", value: "Toda a base de participantes, independentemente dos filtros da lista" },
        { label: "Total de pessoas na base", value: total },
        { label: exportIsAttendance ? "Participação confirmada" : "Respostas válidas", value: highlighted },
        { label: exportIsAttendance ? "Taxa de confirmação" : "Taxa de resposta", value: percentageLabel(percentage) },
        ...(exportIsAttendance ? [{ label: "Responderam sem confirmar", value: exportSummary.no || 0 }] : []),
        { label: "Sem resposta", value: exportSummary.unanswered || 0 },
        { label: "Revisão necessária", value: exportSummary.other || 0 },
        { label: "Fuso horário", value: eventTimezone },
        { label: "Situação ou resposta", value: answerFilters[choice]?.label || "Todas as respostas" },
        { label: "Resposta inicial", value: responseStart ? dateLabel(responseStart, eventTimezone) : "Sem limite" },
        { label: "Resposta final", value: responseEnd ? dateLabel(responseEnd, eventTimezone) : "Sem limite" },
        { label: "Canal", value: CHANNELS[source] || "Todos os canais" },
        { label: "Informações do participante", value: showMoreInformation ? "Com informações adicionais do registro" : "Padrão" },
        { label: "Participantes na lista exportada", value: rows.length },
      ];
      const extraExportColumns = participantInfoColumns.filter((column) => !["user_id", "email"].includes(column.registrationAccessor));
      const headers = ["ID do participante", "Participante", "E-mail", "Status", exportIsAttendance ? "Confirmação" : "Status da resposta", "Resposta atual", "Valor registrado", "Última resposta aceita em", "Canal", "Método", ...extraExportColumns.map((column) => column.exportHeader || column.Header)];
      await generateExcel({
        filename: `RSVP ${exportResult?.definition?.name || selectedId} - ${eventId}`,
        sheet: {
          data: [
            ...buildExcelExportMetadataRows(metadata),
            [],
            headers.map(getExcelCell),
            ...rows.map((item) => [
              item.user_id, item.name || item.user_id, item.email, participantAccessStatusLabel(item),
              responseStatus(item.current_choice, exportIsAttendance).label,
              currentAnswerLabel(item), storedValueLabel(item.response?.value),
              dateLabel(item.accepted_at || item.responded_at || item.response?.responded_at, eventTimezone),
              channelLabel(item.origin_channel || item.source || item.response?.source),
              inputMethodLabel(item.input_method || item.response?.input_method),
              ...extraExportColumns.map((column) => column.exportValue({ ...item, registration: participantRecordsById.get(String(item.user_id)) })),
            ].map(getExcelCell)),
          ]
        },
      });
    } catch (failure) { setError(responseError(failure, "Não foi possível exportar as respostas.")); }
    finally { setExporting(false); }
  };
  useImperativeHandle(ref, () => ({ exportResponses, openBulkResetConfirmation }));

  return <div className="credential-report mb-4">
    {error && <Alert variant="danger" className="p-2">{error}</Alert>}
    {notice && <Alert variant={notice.variant} dismissible onClose={() => setNotice(null)} className="p-2">{notice.message}</Alert>}
    {showDefinitionSelector && <Row className="mx-0 gx-2">
      <Form.Group as={Col} xs={6} md={6} className="mb-3">
        <Form.Label htmlFor="rsvp-report-definition" className="visually-hidden">Selecionar RSVP</Form.Label>
        <Select
          inputId="rsvp-report-definition"
          value={definitionOptions.find((option) => option.value === selectedId) || null}
          options={definitionOptions}
          getOptionLabel={(option) => `${option.label} · ${option.typeLabel}`}
          formatOptionLabel={(option) => <div className="text-break"><span className="fw-semibold">{option.label}</span><div className="small">{option.typeLabel}</div></div>}
          onChange={changeDefinition}
          isLoading={registryState.loading}
          isDisabled={registryState.loading || !definitionOptions.length || saving || exporting || resettingParticipant || bulkResetting || Boolean(resetTarget) || showBulkReset}
          isClearable
          placeholder={registryState.loading ? "Carregando RSVPs…" : "Selecione um RSVP para consultar"}
          noOptionsMessage={() => "Nenhum RSVP encontrado"}
          menuPortalTarget={document.body}
          menuPosition="fixed"
          styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
        />
      </Form.Group>
    </Row>}
    {registryState.error && <Alert variant="warning" className="p-2 d-flex justify-content-between align-items-center gap-2 flex-wrap"><span>{registryState.error}</span><Button size="sm" variant="outline-secondary" onClick={() => setRegistryReload((value) => value + 1)}>Tentar novamente</Button></Alert>}
    {!selectedDefinition && !registryState.loading && !registryState.error && <Alert variant="info" className="p-2">{definitionOptions.length ? "Selecione um RSVP para visualizar a distribuição das respostas e a lista de participantes." : "Nenhum RSVP configurado para este evento."}</Alert>}
    {selectedDefinition && <>
      {showSummary && summary && selectedDefinition && <RsvpSummary summary={summary} isAttendance={isAttendance} />}
      {showDistribution && <>
        <div className="pb-3 px-2 pt-2">
          <div className="mb-3">
            <EventRsvpResponsesDistribution key={`distribution:${eventId}:${selectedId}`} eventId={eventId} definitionId={selectedId} refreshKey={JSON.stringify([refreshKey, reload])} />
          </div>
        </div>
        <hr className="my-2" />
      </>}
      <div className={showDistribution ? "pt-3" : undefined}>
        <ReportDateRangeFilter
          idPrefix="rsvp-response"
          dateRange={pendingResponseDateRange}
          startLabel="Resposta aceita a partir de"
          endLabel="Resposta aceita até"
          timeZone={eventTimezone}
          onChange={(key, value) => setPendingResponseDateRange((previous) => ({ ...previous, [key]: value }))}
          onApply={applyResponseDateRange}
          onReset={restoreResponseDateRange}
        />
      </div>
      {dateRangeError && <Alert variant="warning" className="p-2">{dateRangeError}</Alert>}
      <div ref={tableContainerRef} aria-busy={loading}>
      <ColumnSortingTable
        key={`${eventId}:${selectedId || "none"}`}
        columns={visibleColumns}
        data={participants}
        title={`RSVP ${selectedDefinition.name}`}
        getCellProps={(cell) => ({ className: cell.column.id === "icons" ? "table-icons" : "" })}
        initialState={{ pageIndex: 0, pageSize: 50 }}
        pageResetTrigger={JSON.stringify([result?.query?.responseStart, result?.query?.responseEnd, reload])}
      />
      </div>
    </>}
    <Modal show={Boolean(participant)} onHide={saving ? undefined : () => setParticipant(null)} centered scrollable size="lg" fullscreen="sm-down" backdrop={saving ? "static" : true} keyboard={!saving}>
      <Modal.Header closeButton={!saving}><Modal.Title>Histórico de respostas</Modal.Title></Modal.Header>
      <Modal.Body>
        <Row as="dl" className="gy-2 mb-3">
          <Col as="dt" xs={4} sm={3} className="fw-normal text-muted">Participante</Col>
          <Col as="dd" xs={8} sm={9} className="mb-0 text-break">{participant?.name || `Participante #${participant?.user_id || ""}`}</Col>
          {participant?.email && <>
            <Col as="dt" xs={4} sm={3} className="fw-normal text-muted">E-mail</Col>
            <Col as="dd" xs={8} sm={9} className="mb-0 text-break">{participant.email}</Col>
          </>}
          <Col as="dt" xs={4} sm={3} className="fw-normal text-muted">RSVP</Col>
          <Col as="dd" xs={8} sm={9} className="mb-0">
            <span className="text-break">{selectedDefinition?.name || "—"}</span>
            {isAttendance && <Badge bg="light" text="dark" className="ms-2">Confirmação de participação</Badge>}
          </Col>
        </Row>
        <hr className="my-3" />
        {historyError && <Alert variant="danger" className="p-3">
          <div>{historyError}</div>
          {!historyLoading && !historyDetails && <Button size="sm" variant="outline-danger" className="mt-2" onClick={() => setParticipant((current) => current ? { ...current } : null)}>Tentar novamente</Button>}
        </Alert>}
        {historyLoading ? <div className="text-center text-muted py-5" role="status">
          <Spinner animation="border" size="sm" className="mb-2" aria-hidden="true" />
          <div>Carregando histórico…</div>
        </div> : historyDetails && <>
          <RsvpHistoryContent history={history} historyDetails={historyDetails} eventTimezone={eventTimezone} isAttendance={isAttendance} />
          {historyDetails.has_more && <div className="text-center mt-3">
            <Button size="sm" variant="outline-primary" disabled={historyLoadingMore || saving} onClick={loadMoreHistory}>
              {historyLoadingMore && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
              {historyLoadingMore ? "Carregando…" : "Carregar registros anteriores"}
            </Button>
          </div>}
          {result?.can_edit && <details className="border rounded p-3 mt-4">
            <summary className="fw-semibold">Corrigir resposta</summary>
            <Form.Group className="mt-3" controlId={`rsvp-history-correction-${participant?.user_id}`}>
              <Form.Label>Nova resposta</Form.Label>
              <Form.Select value={correction} disabled={saving} onChange={(event) => { setCorrection(event.target.value); setCorrectionKey(requestKey()); }}>
                <option value="">Selecione uma opção</option>
                <optgroup label="Respostas do RSVP">{answerOptions.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}</optgroup>
                <optgroup label="Outras ações"><option value="reset">Remover resposta atual</option></optgroup>
              </Form.Select>
              <Form.Text>{correction === "reset" ? "O participante voltará a ficar sem resposta. Os registros anteriores serão mantidos no histórico." : "A nova resposta será aplicada a este RSVP e registrada no histórico."}</Form.Text>
            </Form.Group>
            <Button variant="primary" className="mt-3" disabled={saving || !correction} onClick={saveCorrection}>
              {saving && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
              {saving ? "Salvando…" : "Salvar correção"}
            </Button>
          </details>}
        </>}
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={saving} onClick={() => setParticipant(null)}>Fechar</Button></Modal.Footer>
    </Modal>
    <Modal show={Boolean(resetTarget)} onHide={resettingParticipant ? undefined : () => { setResetTarget(null); setResetActionError(""); }} centered backdrop={resettingParticipant ? "static" : true} keyboard={!resettingParticipant}>
      <Modal.Header closeButton={!resettingParticipant}><Modal.Title>Remover resposta atual</Modal.Title></Modal.Header>
      <Modal.Body>
        {resetActionError && <Alert variant="danger" className="p-2">{resetActionError}</Alert>}
        <p className="mb-2">Remover a resposta de <strong>{resetTarget?.name || `Participante #${resetTarget?.user_id || ""}`}</strong> neste RSVP?</p>
        {resetTarget?.email && <p className="small text-muted mb-2">{resetTarget.email}</p>}
        <p className="small mb-2"><strong>{selectedDefinition?.name || "RSVP"}:</strong> {resetTarget ? responseStatus(resetTarget.current_choice, isAttendance).label : ""} · {resetTarget ? currentAnswerLabel(resetTarget) : ""}</p>
        <p className="small text-muted mb-0">O participante ficará sem resposta e poderá responder novamente. O histórico será preservado.</p>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" disabled={resettingParticipant} onClick={() => { setResetTarget(null); setResetActionError(""); }}>Cancelar</Button>
        <Button variant="danger" disabled={resettingParticipant || !resetTarget} onClick={confirmIndividualReset}>
          {resettingParticipant && <Spinner animation="border" size="sm" className="me-2" aria-hidden="true" />}
          {resettingParticipant ? "Removendo…" : "Remover resposta"}
        </Button>
      </Modal.Footer>
    </Modal>
  </div>;
});

export default EventRsvpResponsesCard;
